import React, { useRef } from 'react';
import styled from 'styled-components';
import type { CategoryPickerBodyProps } from './categoryPickerTypes';

// ENG-2551: the dropdown form of the folded category picker. --chrome-offset is
// set by the .order-container / .margin-for-incentive-tracker* classes
// (definitions.scss) so this pins just below whatever fixed chrome is present.
//
// Painted in the page's own ground (--page-ground, definitions.scss) rather
// than white, so at rest it is not a box at all — just the pill sitting under
// the step's instruction pill, spanning exactly the grid's width. The bottom
// 14px fades to transparent: invisible at rest, and once the row is stuck it
// shows the thumbnails sliding in underneath.
const DropdownRow = styled.div`
  position: sticky;
  top: var(--chrome-offset, 52px);
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 0 14px;
  background: linear-gradient(
    to bottom,
    var(--page-ground, #f8f8f8) calc(100% - 14px),
    transparent
  );

  // Same containment as CategoryChips' row, for the same reason: a <select> is
  // intrinsically as wide as its longest <option>, so with a long category name
  // its min-content would propagate up through the shrink-to-fit ItemPanel on
  // the phone breakpoint and drag the sibling thumbnail grid off-viewport.
  // width: 0 hides that intrinsic width from ancestors sizing themselves;
  // min-width: 100% restores it during the real layout pass.
  box-sizing: border-box;
  width: 0;
  min-width: 100%;
`;

// A closed <select> shows only the selected option, so nothing about it hints
// that seven other background sets exist. The count carries that.
const LabelRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  // Optically inside the pill's border below, not flush with its outer edge.
  padding: 0 2px;
`;

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
  color: #6b7c8a;
`;

const OptionCount = styled.span`
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
  color: #8497a4;
  font-variant-numeric: tabular-nums;
`;

// Wraps the select purely to host the chevron, since a select's own ::after is
// not rendered.
const SelectShell = styled.div`
  position: relative;
  display: flex;
  min-width: 0;

  // The chevron is decoration — never let it swallow a tap meant for the
  // select underneath it.
  &::after {
    content: '';
    position: absolute;
    right: 20px;
    top: 50%;
    width: 0;
    height: 0;
    margin-top: -2px;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 6px solid #479de3;
    pointer-events: none;
  }
`;

// appearance: none so the closed state is fully on-brand — a pill, the sibling
// of the "Selecting background 1 of 1" pill it sits beneath; the open state is
// deliberately the OS-native picker, which is what buys the bottom-of-screen
// thumb reach and the free assistive-tech support.
const CategorySelect = styled.select`
  appearance: none;
  -webkit-appearance: none;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 13px 44px 13px 18px;
  border: 1px solid #479de3;
  border-radius: 999px;
  background: white;
  color: #479de3;
  font-size: 16px;
  font-weight: 800;
  line-height: 1.2;
  text-overflow: ellipsis;

  &:focus-visible {
    outline: 2px solid #479de3;
    outline-offset: 2px;
  }
`;

// React 16 has no useId; ids only need to be unique within a document, and a
// per-mount counter gives that without pulling in a dependency.
let nextFieldId = 0;
const useFieldId = () => {
  const idRef = useRef<string>();
  if (!idRef.current) {
    nextFieldId += 1;
    idRef.current = `category-picker-${nextFieldId}`;
  }
  return idRef.current;
};

// onExpand is deliberately not destructured below: the <select> already lists
// every set, so there is nothing to expand to.
const CategoryDropdown = ({
  categories = [],
  selectedIndex,
  onSelect,
}: CategoryPickerBodyProps) => {
  const fieldId = useFieldId();

  // One background set is not a choice — showing a picker with a single option
  // is noise. Nothing to collapse, so render nothing.
  if (categories.length <= 1) return null;

  // Options are valued by index, and so is the selection: index identity is
  // what keeps two studio-authored sets with the same name distinguishable.
  // An out-of-range index falls back to the first set.
  const clamped =
    selectedIndex >= 0 && selectedIndex < categories.length ? selectedIndex : 0;

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const index = Number(event.target.value);
    onSelect(categories[index], index);
  };

  return (
    <DropdownRow>
      <LabelRow>
        <FieldLabel htmlFor={fieldId}>Background set</FieldLabel>
        <OptionCount>{categories.length} to choose from</OptionCount>
      </LabelRow>
      <SelectShell>
        <CategorySelect
          id={fieldId}
          value={String(clamped)}
          onChange={handleChange}
        >
          {categories.map((category, index) => (
            <option key={`${category.name}-${index}`} value={String(index)}>
              {category.name}
            </option>
          ))}
        </CategorySelect>
      </SelectShell>
    </DropdownRow>
  );
};

export default CategoryDropdown;
